/**
 * 🔒 A print template's content (piece 27; decision 0656): the typeface and
 * the sizes the sheet draws in, the title's size and colour, where the page
 * number stands, and the per-template rows of Regions 4, 8, 9 and 10 — which
 * facts, columns and totals rows print, under what label, at what width and
 * at what size.
 *
 * 🔒 One place answers for both halves. The editor judges a draft by these
 * rules, the server refuses a Save by them, and the renderer applies the rows
 * through the functions below, so a hidden row cannot be hidden on one of the
 * three and drawn on another.
 *
 * 🔨 A missing row is the default. A template nobody has touched carries no
 * row at all, and every default here is the sheet as it printed before this
 * existed, which is why the golden fixtures at defaults did not move.
 */
import type { PrintFact, PrintLineColumn } from '../documents/printRegions';
import { REGION_8_EXTRA_COLUMNS, REGION_8_PRINT_COLUMNS } from '../documents/printRegions';
import type { StringKey } from '../strings';

/**
 * 🔒 The faces a template may name (decision 0656). Liberation Sans is the
 * face the deployed image installs and the application serves (decision
 * 0627); Serif and Monospace are the image's Liberation Serif and Liberation
 * Mono, which are metric-compatible with Times New Roman and Courier New, so a
 * line breaks on a reader's screen where it breaks on the printed sheet.
 */
export const PRINT_FONT_FAMILIES = ['LiberationSans', 'Serif', 'Monospace'] as const;
export type PrintFontFamily = (typeof PRINT_FONT_FAMILIES)[number];

export const PRINT_FONT_FAMILY_LABEL_KEYS: Readonly<Record<PrintFontFamily, StringKey>> = {
  LiberationSans: 'print.font.liberationSans',
  Serif: 'print.font.serif',
  Monospace: 'print.font.monospace',
};

/**
 * 🔒 The five colours a title may take (decision 0656). They are names and
 * never a typed hex: the renderer maps each to the one palette it holds
 * (decision 0560), so a title cannot be printed in a colour nobody chose for
 * paper.
 */
export const PRINT_TITLE_COLORS = ['Ink', 'Charcoal', 'Navy', 'Green', 'Maroon'] as const;
export type PrintTitleColor = (typeof PRINT_TITLE_COLORS)[number];

export const PRINT_TITLE_COLOR_LABEL_KEYS: Readonly<Record<PrintTitleColor, StringKey>> = {
  Ink: 'print.titleColor.ink',
  Charcoal: 'print.titleColor.charcoal',
  Navy: 'print.titleColor.navy',
  Green: 'print.titleColor.green',
  Maroon: 'print.titleColor.maroon',
};

/**
 * 🔒 Where "Page n of m" stands on a sheet of several (decision 0656). 🔨 Off
 * prints none on any sheet, however long the document runs.
 */
export const PRINT_PAGE_NUMBER_POSITIONS = ['Off', 'Left', 'Centre', 'Right'] as const;
export type PrintPageNumberPosition = (typeof PRINT_PAGE_NUMBER_POSITIONS)[number];

export const PRINT_PAGE_NUMBER_LABEL_KEYS: Readonly<Record<PrintPageNumberPosition, StringKey>> = {
  Off: 'print.pageNumber.off',
  Left: 'print.pageNumber.left',
  Centre: 'print.pageNumber.centre',
  Right: 'print.pageNumber.right',
};

/** 🔒 The body's size in whole points, the renderer's own ten by default (decision 0560). */
export const PRINT_BODY_POINTS = { min: 8, max: 14, default: 10 } as const;
/** 🔒 The title's size in whole points, the renderer's own twenty-six by default (decision 0560). */
export const PRINT_TITLE_POINTS = { min: 14, max: 36, default: 26 } as const;
/** 🔒 Region 10's two blocks in whole points, the renderer's own nine by default. */
export const PRINT_SUMMARY_POINTS = { min: 6, max: 14, default: 9 } as const;
/** 🔒 A column's rough share of the table, in whole percent; the shown columns' shares are normalised at render. */
export const PRINT_COLUMN_WIDTH_PERCENT = { min: 1, max: 100 } as const;
/** A typed label runs to this many characters, which is more than any caption a sheet draws. */
export const PRINT_ROW_LABEL_MAX = 60;
/** 🔨 A template's name runs to this many characters, the label's own cap, which the gallery's card reads whole. */
export const PRINT_TEMPLATE_NAME_MAX = 60;

/** The facts a template carries about how its sheet is drawn, as the editor sends them and the renderer reads them. */
export interface PrintTemplateStyle {
  fontFamily: PrintFontFamily;
  bodyPointSize: number;
  titlePointSize: number;
  titleColor: PrintTitleColor;
  pageNumberPosition: PrintPageNumberPosition;
  /** 🔒 A short grey dash at each page edge where a sheet folded in thirds creases (T190; decision 0661); off by default. */
  foldMarks: boolean;
}

/** 🔒 The sheet as it printed before any of this existed, which is what a new template carries. */
export const PRINT_TEMPLATE_STYLE_DEFAULTS: Readonly<PrintTemplateStyle> = {
  fontFamily: 'LiberationSans',
  bodyPointSize: PRINT_BODY_POINTS.default,
  titlePointSize: PRINT_TITLE_POINTS.default,
  titleColor: 'Ink',
  pageNumberPosition: 'Right',
  foldMarks: false,
};

function wholeWithin(value: unknown, bounds: { min: number; max: number }): boolean {
  return typeof value === 'number' && Number.isInteger(value) && value >= bounds.min && value <= bounds.max;
}

/**
 * 🔒 The refusal a template's style carries, or null where it holds (decision
 * 0656). 🔨 It names the field, because five fields refused by one sentence is
 * a sentence nobody can act on.
 */
export function printTemplateStyleRefusal(style: unknown): string | null {
  if (typeof style !== 'object' || style === null) return 'a template’s style carries its font, its sizes, its title color and its page number';
  const s = style as Partial<Record<keyof PrintTemplateStyle, unknown>>;
  if (!(PRINT_FONT_FAMILIES as readonly unknown[]).includes(s.fontFamily)) return `the font is one of ${PRINT_FONT_FAMILIES.join(', ')}`;
  if (!wholeWithin(s.bodyPointSize, PRINT_BODY_POINTS)) return `the body size is a whole number of points between ${PRINT_BODY_POINTS.min} and ${PRINT_BODY_POINTS.max}`;
  if (!wholeWithin(s.titlePointSize, PRINT_TITLE_POINTS)) return `the title size is a whole number of points between ${PRINT_TITLE_POINTS.min} and ${PRINT_TITLE_POINTS.max}`;
  if (!(PRINT_TITLE_COLORS as readonly unknown[]).includes(s.titleColor)) return `the title color is one of ${PRINT_TITLE_COLORS.join(', ')}`;
  if (!(PRINT_PAGE_NUMBER_POSITIONS as readonly unknown[]).includes(s.pageNumberPosition)) return `the page number is one of ${PRINT_PAGE_NUMBER_POSITIONS.join(', ')}`;
  if (typeof s.foldMarks !== 'boolean') return 'the fold marks are on or off';
  return null;
}

/**
 * 🔒 Region 4's rows, one member per fact any projection writes (decision
 * 0656). A projection keys each fact it writes; a fact written without a key
 * is drawn and cannot be hidden.
 */
export const PRINT_INFORMATION_ROW_KEYS = [
  'date', 'expiryDate', 'dueDate', 'terms', 'customerPo', 'deal', 'quote', 'salesOrder', 'purchaseOrder', 'invoice', 'bill',
  'createdBy', 'enteredBy', 'requestedBy', 'method', 'reason', 'vendorInvoiceNumber', 'carrier', 'tracking', 'periodEnd',
  'period', 'openingBalance', 'sentAt', 'responsesDue', 'customer', 'checkNumber',
  // Decision 0682: the Purchase Order's Deliver by, and how five documents' goods travel.
  'deliveryDate', 'shipmentPreference',
  // Decision 2612 (T1911): the Deal's ceiling on a Quote and a Sales Order.
  'notToExceed',
] as const;
export type PrintInformationRowKey = (typeof PRINT_INFORMATION_ROW_KEYS)[number];

/**
 * 🔒 Region 9's rows (decision 0656). `tax` is every tax line as one switch.
 * 🔨 `total` is the bold row the projection marked, and it cannot be hidden.
 */
export const PRINT_TOTALS_ROW_KEYS = ['subtotal', 'discount', 'tax', 'total', 'paid', 'open', 'settled', 'openingBalance'] as const;
export type PrintTotalsRowKey = (typeof PRINT_TOTALS_ROW_KEYS)[number];

/** 🔒 Region 10's two blocks: the document's own closing note, and the Term Container's clauses beneath it. */
export const PRINT_SUMMARY_ROW_KEYS = ['notes', 'terms'] as const;
export type PrintSummaryRowKey = (typeof PRINT_SUMMARY_ROW_KEYS)[number];

/**
 * Region 8's rows are its columns, `unit` is the Qty cell's unit beneath
 * the figure, shown or not, and `listPrice` is a sales line's list price
 * beneath its Unit Price, shown or not (decision 1063). 🔨 The SKU and the vendor's part number are lines
 * of the Item cell and never columns (decisions 0640, 0641), so neither is a row.
 */
export const PRINT_COLUMN_ROW_KEYS: readonly PrintLineColumn[] = [...REGION_8_PRINT_COLUMNS, ...REGION_8_EXTRA_COLUMNS].filter((column) => column !== 'sku' && column !== 'vendorPart');

/** The four regions that carry rows. */
export const PRINT_ROW_REGIONS = [4, 8, 9, 10] as const;
export type PrintRowRegion = (typeof PRINT_ROW_REGIONS)[number];

/** The key a printed fact carries, so a template's row can find it. */
export type PrintFactKey = PrintInformationRowKey | PrintTotalsRowKey;

export function printRowKeysFor(regionNumber: number): readonly string[] {
  if (regionNumber === 4) return PRINT_INFORMATION_ROW_KEYS;
  if (regionNumber === 8) return PRINT_COLUMN_ROW_KEYS;
  if (regionNumber === 9) return PRINT_TOTALS_ROW_KEYS;
  if (regionNumber === 10) return PRINT_SUMMARY_ROW_KEYS;
  return [];
}

/** The name a Region 4 fact goes by in the editor, before any label a person types over it. */
const INFORMATION_ROW_LABEL_KEYS: Readonly<Record<PrintInformationRowKey, StringKey>> = {
  date: 'print.row.date',
  expiryDate: 'print.row.expiryDate',
  dueDate: 'print.row.dueDate',
  terms: 'print.row.paymentTerms',
  customerPo: 'print.row.customerPo',
  deal: 'print.row.deal',
  quote: 'print.row.quote',
  salesOrder: 'print.row.salesOrder',
  purchaseOrder: 'print.row.purchaseOrder',
  invoice: 'print.row.invoice',
  bill: 'print.row.bill',
  createdBy: 'print.row.createdBy',
  enteredBy: 'print.row.enteredBy',
  requestedBy: 'print.row.requestedBy',
  method: 'print.row.method',
  reason: 'print.row.reason',
  vendorInvoiceNumber: 'print.row.vendorInvoiceNumber',
  carrier: 'print.row.carrier',
  tracking: 'print.row.tracking',
  periodEnd: 'print.row.periodEnd',
  period: 'print.row.period',
  openingBalance: 'print.row.openingBalance',
  sentAt: 'print.row.sentAt',
  responsesDue: 'print.row.responsesDue',
  customer: 'print.row.customer',
  checkNumber: 'print.row.checkNumber',
  deliveryDate: 'print.row.deliveryDate',
  shipmentPreference: 'print.row.shipmentPreference',
  notToExceed: 'print.row.notToExceed',
};

const TOTALS_ROW_LABEL_KEYS: Readonly<Record<PrintTotalsRowKey, StringKey>> = {
  subtotal: 'print.row.subtotal',
  discount: 'print.row.discount',
  tax: 'print.row.tax',
  total: 'print.row.total',
  paid: 'print.row.paid',
  open: 'print.row.open',
  settled: 'print.row.settled',
  openingBalance: 'print.row.openingBalance',
};

const SUMMARY_ROW_LABEL_KEYS: Readonly<Record<PrintSummaryRowKey, StringKey>> = {
  notes: 'print.row.notes',
  terms: 'print.row.clauses',
};

/**
 * The string a row of Regions 4, 9 and 10 goes by in the editor; null for a
 * Region 8 column, whose heading is the column's own (decision 0013).
 */
export function printRowLabelKey(regionNumber: number, rowKey: string): StringKey | null {
  if (regionNumber === 4) return INFORMATION_ROW_LABEL_KEYS[rowKey as PrintInformationRowKey] ?? null;
  if (regionNumber === 9) return TOTALS_ROW_LABEL_KEYS[rowKey as PrintTotalsRowKey] ?? null;
  if (regionNumber === 10) return SUMMARY_ROW_LABEL_KEYS[rowKey as PrintSummaryRowKey] ?? null;
  return null;
}

/**
 * 🔒 Region 4's rows each document type's projection writes, in the order it
 * writes them (decision 0656). The editor lists these under Region 4, and a
 * duplicate onto another type starts from that type's own list.
 */
const INFORMATION_ROWS_BY_TYPE: Readonly<Record<string, readonly PrintInformationRowKey[]>> = {
  Quote: ['date', 'expiryDate', 'terms', 'notToExceed', 'shipmentPreference'],
  SalesOrder: ['date', 'terms', 'customerPo', 'notToExceed', 'deal', 'quote', 'shipmentPreference'],
  Invoice: ['date', 'dueDate', 'terms', 'salesOrder', 'shipmentPreference'],
  PurchaseOrder: ['date', 'deliveryDate', 'createdBy', 'method', 'deal', 'salesOrder', 'shipmentPreference'],
  CreditNote: ['date', 'invoice', 'reason'],
  Bill: ['vendorInvoiceNumber', 'date', 'dueDate', 'purchaseOrder', 'enteredBy'],
  ChangeOrder: ['date', 'salesOrder', 'customerPo'],
  Shipment: ['date', 'carrier', 'tracking'],
  VendorCredit: ['date', 'bill'],
  ExpenseReport: ['date', 'periodEnd'],
  Payment: ['date', 'method'],
  Statement: ['period', 'openingBalance', 'sentAt'],
  RFQ: ['date', 'responsesDue', 'requestedBy'],
  WorkOrder: ['date', 'customer', 'shipmentPreference'],
  // 🔒 Decision 2911: the day the report was signed and the customer; the visit and its completion are facts of their own.
  ServiceReport: ['date', 'customer'],
  Check: ['date', 'checkNumber'],
};

/** 🔒 Region 9's rows each type's projection writes; a type that prints no totals lists none. */
const TOTALS_ROWS_BY_TYPE: Readonly<Record<string, readonly PrintTotalsRowKey[]>> = {
  Quote: ['subtotal', 'discount', 'tax', 'total'],
  SalesOrder: ['subtotal', 'discount', 'tax', 'total'],
  Invoice: ['subtotal', 'discount', 'tax', 'total'],
  CreditNote: ['subtotal', 'discount', 'tax', 'total'],
  ChangeOrder: ['subtotal', 'discount', 'tax', 'total'],
  PurchaseOrder: ['subtotal', 'total'],
  VendorCredit: ['subtotal', 'total'],
  ExpenseReport: ['subtotal', 'total'],
  Bill: ['subtotal', 'total', 'paid', 'open'],
  Payment: ['settled', 'total'],
  Statement: ['openingBalance', 'total'],
  WorkOrder: ['total'],
  // 🔨 Decision 2911: the Service Report prints no price, so its Region 9 lists nothing.
  ServiceReport: [],
  Check: ['total'],
  Shipment: [],
  RFQ: [],
};

export function printInformationRowsFor(documentType: string): readonly PrintInformationRowKey[] {
  return INFORMATION_ROWS_BY_TYPE[documentType] ?? [];
}

export function printTotalsRowsFor(documentType: string): readonly PrintTotalsRowKey[] {
  return TOTALS_ROWS_BY_TYPE[documentType] ?? [];
}

/**
 * One row a template changes from its default: a Region 4 fact, a Region 8
 * column, a Region 9 totals row or a Region 10 block (decision 0656).
 */
export interface PrintTemplateContentRow {
  regionNumber: number;
  rowKey: string;
  isShown: boolean;
  labelOverride: string | null;
  widthPercent: number | null;
  pointSize: number | null;
}

/** A row carrying nothing a default does not, which a Save stores as no row at all. */
export function printRowIsDefault(row: PrintTemplateContentRow): boolean {
  return row.isShown && (row.labelOverride ?? '').trim() === '' && row.widthPercent === null && row.pointSize === null;
}

/**
 * 🔒 The refusal a set of rows carries, or null where every row holds
 * (decision 0656). The sentence names the region and the row, because a
 * person reading "out of bounds" over forty rows cannot tell which to fix.
 */
export function printContentRowsRefusal(rows: readonly PrintTemplateContentRow[]): string | null {
  const seen = new Set<string>();
  for (const row of rows) {
    if (!(PRINT_ROW_REGIONS as readonly number[]).includes(row.regionNumber)) return `a content row belongs to Region ${PRINT_ROW_REGIONS.join(', ')}, and this one names ${String(row.regionNumber)}`;
    const named = `region ${row.regionNumber}’s ${String(row.rowKey)} row`;
    if (!printRowKeysFor(row.regionNumber).includes(row.rowKey)) return `region ${row.regionNumber} has no row named ${String(row.rowKey)}`;
    const key = `${row.regionNumber}:${row.rowKey}`;
    if (seen.has(key)) return `${named} is sent twice`;
    seen.add(key);
    if (typeof row.isShown !== 'boolean') return `${named} is shown or hidden`;
    // 🔨 The Total is the figure the sheet is for, and a sheet without it is a list of lines.
    if (row.regionNumber === 9 && row.rowKey === 'total' && !row.isShown) return 'region 9’s Total cannot be hidden: it is the figure the sheet is for';
    if (row.labelOverride !== null) {
      if (typeof row.labelOverride !== 'string' || row.labelOverride.length > PRINT_ROW_LABEL_MAX) return `${named}: a label runs to ${PRINT_ROW_LABEL_MAX} characters`;
      // 🔨 Region 10 draws no caption, the unit is a line beneath a figure, and the tax rows are one switch over several labels.
      if (row.regionNumber === 10 || (row.regionNumber === 8 && (row.rowKey === 'unit' || row.rowKey === 'listPrice')) || (row.regionNumber === 9 && row.rowKey === 'tax')) return `${named} carries no label of its own`;
    }
    if (row.widthPercent !== null) {
      if (row.regionNumber !== 8 || row.rowKey === 'unit' || row.rowKey === 'listPrice') return `${named}: a width belongs to a Region 8 column`;
      if (!wholeWithin(row.widthPercent, PRINT_COLUMN_WIDTH_PERCENT)) return `${named}: a width is a whole percent between ${PRINT_COLUMN_WIDTH_PERCENT.min} and ${PRINT_COLUMN_WIDTH_PERCENT.max}`;
    }
    if (row.pointSize !== null) {
      if (row.regionNumber !== 10) return `${named}: a size belongs to Region 10’s notes and terms`;
      if (!wholeWithin(row.pointSize, PRINT_SUMMARY_POINTS)) return `${named}: a size is a whole number of points between ${PRINT_SUMMARY_POINTS.min} and ${PRINT_SUMMARY_POINTS.max}`;
    }
  }
  return null;
}

/** The row a template carries for one region's key, if it carries one. */
export function printRowFor(rows: readonly PrintTemplateContentRow[] | undefined, regionNumber: number, rowKey: string): PrintTemplateContentRow | undefined {
  return (rows ?? []).find((row) => row.regionNumber === regionNumber && row.rowKey === rowKey);
}

/**
 * 🔒 Region 4's or Region 9's facts as the template prints them (decision
 * 0656): a hidden row absent, a typed label in place of the projection's own.
 * 🔨 The Total prints whatever a row says, and the tax lines take no label.
 */
export function printShownFacts(facts: readonly PrintFact[], rows: readonly PrintTemplateContentRow[] | undefined, regionNumber: 4 | 9): PrintFact[] {
  if (!rows || rows.length === 0) return [...facts];
  const out: PrintFact[] = [];
  for (const fact of facts) {
    const row = fact.key === undefined ? undefined : printRowFor(rows, regionNumber, fact.key);
    const isTotal = regionNumber === 9 && fact.key === 'total';
    if (row && !row.isShown && !isTotal) continue;
    const label = row && fact.key !== 'tax' ? (row.labelOverride ?? '').trim() : '';
    out.push(label === '' ? fact : { ...fact, label });
  }
  return out;
}

/**
 * 🔒 Region 8's column set less the columns the template hides (decision
 * 0656). 🔨 The Item and the Description are one drawn cell (decision 0560),
 * so hiding the Item hides the description with it.
 */
export function printShownColumns(columns: readonly PrintLineColumn[], rows: readonly PrintTemplateContentRow[] | undefined): readonly PrintLineColumn[] {
  if (!rows || rows.length === 0) return columns;
  const hidden = new Set(rows.filter((row) => row.regionNumber === 8 && !row.isShown).map((row) => row.rowKey));
  if (hidden.has('item')) hidden.add('description');
  return hidden.size === 0 ? columns : columns.filter((column) => !hidden.has(column));
}

/** Whether the Qty cell draws its unit beneath the figure; it does unless the template says otherwise. */
export function printShowsUnit(rows: readonly PrintTemplateContentRow[] | undefined): boolean {
  return printRowFor(rows, 8, 'unit')?.isShown !== false;
}

/** 🔒 Whether the Unit Price cell draws a sales line's list price beneath it; it does unless the template says otherwise (decision 1063). */
export function printShowsListPrice(rows: readonly PrintTemplateContentRow[] | undefined): boolean {
  return printRowFor(rows, 8, 'listPrice')?.isShown !== false;
}

/** The heading a template typed over one column, or null where it typed none. */
export function printColumnLabelOverride(rows: readonly PrintTemplateContentRow[] | undefined, column: PrintLineColumn): string | null {
  const typed = (printRowFor(rows, 8, column)?.labelOverride ?? '').trim();
  return typed === '' ? null : typed;
}

/**
 * 🔒 The share of the table each drawn column takes, in percent summing to a
 * hundred, or null where the template set no width at all (decision 0656).
 * A person types rough numbers: the columns given a width keep its
 * proportion, the rest share what is left of a hundred, and the whole is then
 * normalised. 🔨 A column left with nothing still takes five, so no column
 * draws at nought.
 */
export function printColumnWidthPercents(drawn: readonly PrintLineColumn[], rows: readonly PrintTemplateContentRow[] | undefined): Partial<Record<PrintLineColumn, number>> | null {
  if (drawn.length === 0) return null;
  const typed = new Map<PrintLineColumn, number>();
  for (const column of drawn) {
    const width = printRowFor(rows, 8, column)?.widthPercent;
    if (typeof width === 'number' && width > 0) typed.set(column, width);
  }
  if (typed.size === 0) return null;
  const given = [...typed.values()].reduce((sum, width) => sum + width, 0);
  const unset = drawn.length - typed.size;
  const each = unset === 0 ? 0 : Math.max(5, (100 - given) / unset);
  const raw = drawn.map((column) => [column, typed.get(column) ?? each] as const);
  const total = raw.reduce((sum, [, width]) => sum + width, 0);
  return Object.fromEntries(raw.map(([column, width]) => [column, Math.round((width / total) * 10000) / 100]));
}

/** Whether one of Region 10's two blocks prints, and at what size where the template names one. */
export function printSummaryRow(rows: readonly PrintTemplateContentRow[] | undefined, key: PrintSummaryRowKey): { shown: boolean; pointSize: number | null } {
  const row = printRowFor(rows, 10, key);
  return { shown: row?.isShown !== false, pointSize: row?.pointSize ?? null };
}
